import { useCallback } from 'react';
import { Platform, Linking } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import * as Sentry from '@sentry/react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '~/components/providers/ThemeProvider';
import { useToastMessages } from '~/hooks/useToastMessages';

/**
 * React hook that returns a function to open web pages with theme-aware styling.
 * Handles platform differences automatically:
 * - Web: Opens in a new tab
 * - Native: Uses Expo WebBrowser with themed styling, or external browser if specified
 *
 * Falls back to the system browser when the in-app browser is unavailable, and
 * shows an error toast when the device has no browser at all.
 *
 * @returns A function that takes a URL, an optional useExternalBrowser flag, and an optional presentation style
 */
export function useOpenWebPage() {
  const { getThemeColor } = useTheme();
  const { showErrorToast } = useToastMessages();
  const { t } = useTranslation();

  return useCallback(
    async (
      url: string,
      useExternalBrowser = false,
      presentationStyle = WebBrowser.WebBrowserPresentationStyle.PAGE_SHEET
    ) => {
      if (Platform.OS === 'web') {
        // Open in a new tab for web platform
        window.open(url, '_blank');
        return;
      }

      const openExternally = async () => {
        try {
          await Linking.openURL(url);
        } catch (error) {
          Sentry.captureException(error, {
            tags: { feature: 'open_web_page' },
            extra: { url },
          });
          showErrorToast(t('common.error.failedToOpenLink'));
        }
      };

      // On native platforms, use external browser if specified (fixes Android file upload issues)
      if (useExternalBrowser) {
        return openExternally();
      }

      // Use WebBrowser for native platforms with theme-aware styling
      const primaryColor = getThemeColor('secondary', '600', 'light');
      try {
        await WebBrowser.openBrowserAsync(url, {
          toolbarColor: primaryColor,
          controlsColor: '#FFFFFF',
          presentationStyle,
        });
      } catch {
        // A rejection means the URL couldn't be opened at all: no Custom-Tabs browser on
        // Android, unparseable URL, etc. (an already-open iOS sheet resolves as 'locked',
        // it does not reject), so retrying with the system handler is always safe.
        await openExternally();
      }
    },
    [getThemeColor, showErrorToast, t]
  );
}
